import React, { useEffect, useMemo, useRef, useState } from "react";

export interface SearchableSelectOption {
  label: string;
  value: string;
}

interface SearchableSelectProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  loadingText?: string;
  noResultsText?: string;
  required?: boolean;
  showRequiredMark?: boolean;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  id,
  label,
  value,
  onChange,
  options,
  placeholder = "Search or select",
  loadingText = "Loading...",
  noResultsText = "No results found",
  required = false,
  showRequiredMark,
  disabled = false,
  loading = false,
  className = "",
  labelClassName = "block text-sm font-medium text-gray-700 mb-1",
  inputClassName = "",
}) => {
  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );
  const [query, setQuery] = useState(selectedOption?.label || "");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setQuery(selectedOption?.label || "");
  }, [selectedOption]);

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery || selectedOption?.label === query) {
      return options;
    }

    return options.filter((option) =>
      option.label.toLowerCase().includes(normalizedQuery),
    );
  }, [options, query, selectedOption]);

  const selectOption = (option: SearchableSelectOption) => {
    onChange(option.value);
    setQuery(option.label);
    setIsOpen(false);
    setHighlightedIndex(0);
    inputRef.current?.blur();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextQuery = event.target.value;
    setQuery(nextQuery);
    setIsOpen(true);
    setHighlightedIndex(0);

    if (!nextQuery) {
      onChange("");
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && ["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) {
      setIsOpen(true);
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((current) =>
        Math.min(current + 1, filteredOptions.length - 1),
      );
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((current) => Math.max(current - 1, 0));
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const highlightedOption = filteredOptions[highlightedIndex];
      if (highlightedOption) {
        selectOption(highlightedOption);
      }
    }

    if (event.key === "Escape") {
      setIsOpen(false);
      setQuery(selectedOption?.label || "");
    }
  };

  const showOptions = isOpen && !disabled;

  return (
    <div className={`relative ${className}`}>
      <label htmlFor={id} className={labelClassName}>
        {label}
        {(showRequiredMark ?? required) && <span className="text-red-500 ml-1">*</span>}
      </label>
      <input
        ref={inputRef}
        id={id}
        type="text"
        role="combobox"
        aria-expanded={showOptions}
        aria-autocomplete="list"
        aria-controls={`${id}-options`}
        value={query}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        onBlur={() => {
          window.setTimeout(() => {
            setIsOpen(false);
            setQuery(selectedOption?.label || "");
          }, 100);
        }}
        onKeyDown={handleKeyDown}
        disabled={disabled || loading}
        required={required}
        placeholder={loading ? loadingText : placeholder}
        autoComplete="off"
        className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${inputClassName}`}
      />

      {showOptions && (
        <div
          id={`${id}-options`}
          role="listbox"
          className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          {loading ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              {loadingText}
            </div>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={option.value === value}
                onMouseDown={(event) => {
                  event.preventDefault();
                  selectOption(option);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`block w-full px-3 py-2 text-left text-sm ${
                  index === highlightedIndex
                    ? "bg-blue-50 text-blue-900"
                    : "text-gray-900 hover:bg-gray-50"
                }`}
              >
                {option.label}
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500">
              {noResultsText}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
